import { Component, EventEmitter, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ValidationResult, Validator, rules } from "@lchemy/model/validation";
import { of as observableOf } from "rxjs";
import { catchError, finalize, startWith, takeUntil } from "rxjs/operators";

import { FORM_ERROR_FORMATTER_MAP } from "@lantern/app/common";
import { GrowlService } from "@lantern/app/core";

import { GlobalAuthService } from "@local/app/core/global-auth";
import { LOGIN_ERROR_MAP } from "@local/app/pages/shared";

interface ForgotPasswordModel {
  email: string;
}

const forgotPasswordValidator: Validator<ForgotPasswordModel> =
  new Validator<ForgotPasswordModel>({
    email: [rules.required(), rules.isString(), rules.isEmail()]
  });

@Component({
  templateUrl: "./request-reset-password.template.html",
  providers: [
    {
      provide: FORM_ERROR_FORMATTER_MAP,
      useValue: LOGIN_ERROR_MAP
    }
  ]
})
export class RequestResetPasswordComponent implements OnInit, OnDestroy {
  loading: boolean = false;
  submitted: boolean = false;

  forgotPasswordForm = this.formBuilder.group({ email: "" });
  forgotPasswordValidator = forgotPasswordValidator;
  validationResult: ValidationResult;
  redirect?: string;

  constructor(
    private growlService: GrowlService,
    private globalAuthService: GlobalAuthService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initFormValueFromParams();
    this.initValidation();
  }

  private onDestroyEmitter = new EventEmitter();
  ngOnDestroy(): void {
    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();
  }

  async submitForm(): Promise<void> {
    if (this.loading) {
      return;
    }

    await this.validate();
    if (!this.validationResult.isValid) {
      this.growlService.warning("Please provide a valid email address");
      return;
    }
    this.callForgotPassword();
  }

  private callForgotPassword(): void {
    this.loading = true;

    this.globalAuthService
      .requestResetPassword(this.forgotPasswordForm.value.email, this.redirect)
      .pipe(
        catchError(() => observableOf({ success: false })),
        finalize(() => (this.loading = false)),
        takeUntil(this.onDestroyEmitter)
      )
      .subscribe(({ success }) =>
        success
          ? (this.submitted = true)
          : this.growlService.danger("Error requesting password reset.")
      );
  }

  private initFormValueFromParams(): void {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe((params) => {
        this.forgotPasswordForm.patchValue({
          email: params.email || ""
        });
        this.redirect = params.redirect;
      });
  }

  private initValidation(): void {
    this.forgotPasswordForm.valueChanges
      .pipe(startWith(true), takeUntil(this.onDestroyEmitter))
      .subscribe(() => this.validate());
  }

  async validate(): Promise<void> {
    this.validationResult = await this.forgotPasswordValidator.validate(
      this.forgotPasswordForm.value
    );
  }
}
